/**
 * Guard Registry
 * Sistema centralizado para registrar y ejecutar guards de rutas
 */

import type { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import type { RouteGuard } from './guards/RouteGuard'
import { RequiresAuthGuard } from './guards/RequiresAuthGuard'
import { RequiresLegalConsentGuard } from './guards/RequiresLegalConsentGuard'
import { EnsureProfessorProfileGuard } from './guards/EnsureProfessorProfileGuard'

class GuardRegistry {
  private guards: RouteGuard[] = []

  /**
   * Registrar un guard globalmente
   */
  register(guard: RouteGuard): void {
    this.guards.push(guard)
  }

  /**
   * Ejecutar todos los guards registrados
   */
  async executeGuards(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ): Promise<void> {
    // Ejecutar guards en orden
    for (const guard of this.guards) {
      const result = await guard.canActivate(to, from)

      if (result === false) {
        // Bloquear navegación
        next(false)
        return
      }

      if (typeof result === 'string') {
        // Redirigir
        next(result)
        return
      }

      // result === true, continuar con el siguiente guard
    }

    // Todos los guards pasaron
    next()
  }

  /**
   * Limpiar todos los guards (útil para testing)
   */
  clear(): void {
    this.guards = []
  }

  /**
   * Obtener lista de guards registrados
   */
  getRegisteredGuards(): RouteGuard[] {
    return [...this.guards]
  }
}

// Singleton instance
export const guardRegistry = new GuardRegistry()

/**
 * Registrar guards por defecto
 */
export function registerDefaultGuards(): void {
  guardRegistry.register(new RequiresAuthGuard())
  // Consentimiento legal (ADR-SEC-018): después de saber que hay sesión y antes
  // de cualquier otra decisión sobre la ruta.
  guardRegistry.register(new RequiresLegalConsentGuard())
  // Perfil de profesor (ADR-SEC-020): después del consentimiento — nada se crea
  // antes de aceptar los términos. Sustituye a RequiresRoleGuard('professor'),
  // retirado el 2026-10-08: comparaba `users.role`, que ya no autoriza nada, con un
  // `meta.role` que ninguna ruta declaraba.
  guardRegistry.register(new EnsureProfessorProfileGuard())
}
